"use client";

import { ArrowRight, CircleCheck as CheckCircle2, Truck, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const trustPills = [{
    icon: CheckCircle2,
    label: "Data-Driven Insights"
  }, {
    icon: Truck,
    label: "OTA Optimization"
  }, {
    icon: Sparkles,
    label: "Revenue Audits"
  }];
  return <section data-section-id="62" id="newshero" className="relative min-h-[80vh] w-full overflow-hidden bg-background text-foreground">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute left-0 right-0 top-1/2 h-[40%] -translate-y-1/2 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_strategy.png)] opacity-20 blur-sm" />

      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(var(--foreground) 1px, transparent 1px)",
      backgroundSize: "3px 3px"
    }} />

      <div className="relative mx-auto flex min-h-[80vh] w-full max-w-7xl items-center px-6 py-24 sm:px-8 lg:px-12">
        {}
        <div ref={leftRef} className={`pointer-events-none absolute left-4 top-1/2 hidden -translate-y-1/2 lg:block transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <div className="relative" style={{
          transform: "rotate(-8deg)"
        }}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_consulting_expert.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_consulting_expert.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_consulting_expert.png-wvc-sizes" alt="Sunil Singh hotel revenue expert" loading="lazy" decoding="async" className="h-auto w-44 rounded-lg shadow-lg xl:w-56" />
          </div>
        </div>

        {}
        <div ref={rightRef} className={`pointer-events-none absolute right-6 top-1/3 hidden lg:block transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
          <div className="relative flex h-36 w-36 items-center justify-center rounded-lg border-2 border-foreground/70 xl:h-44 xl:w-44" style={{
          borderRadius: "0.5rem",
          animation: "spin 24s linear infinite"
        }}>
            <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full">
              <defs>
                <path id="stamp-circle" d="M 100,100 m -72,0 a 72,72 0 1,1 144,0 a 72,72 0 1,1 -144,0" />
              </defs>
              <text className="fill-foreground font-default" style={{
              fontSize: "16px",
              letterSpacing: "0.25em",
              fontWeight: 600
            }}>
                <textPath href="#stamp-circle" startOffset="0">
                  REVENUE GROWTH · EXPERT STRATEGY · REVENUE GROWTH · EXPERT STRATEGY ·
                </textPath>
              </text>
            </svg>
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-foreground text-background" style={{
            borderRadius: "0.5rem"
          }}>
              <Sparkles className="h-5 w-5" />
            </div>
          </div>
        </div>

        {}
        <div ref={textRef} className={`relative mx-auto flex w-full max-w-3xl flex-col items-center text-center transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <Badge variant="outline" className="mb-8 border-foreground/20 bg-background/60 px-4 py-1.5 font-mono text-[0.7rem] tracking-[0.25em] text-[var(--chart-2)] uppercase backdrop-blur-sm" style={{
          color: "hsl(43 46% 55%)"
        }}>
            HotelRevBaBa · Revenue Consultancy
          </Badge>

          {}
          <h1 className="font-default font-serif font-light text-foreground" style={{
          fontSize: "clamp(3rem, 6.5vw, 6.5rem)",
          lineHeight: "0.95",
          letterSpacing: "-0.02em"
        }}>
            Maximize Your Hotel’s
            <br />
            <em className="font-medium italic text-[var(--secondary)]">Revenue</em>{" "}
            with Data-Driven Strategies.
          </h1>

          {}
          <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Expert revenue management consulting by Sunil Singh. We help hotel owners and investors optimize OTA performance, implement cutting-edge tech, and drive sustainable growth.
          </p>

          {}
          <div className="mt-10 flex flex-col items-center gap-5 sm:flex-row sm:gap-6">
            <Link to="/ContactPage">
              <Button size="lg" className="h-12 rounded-lg bg-secondary px-8 text-secondary-foreground hover:bg-secondary/90" style={{
              borderRadius: "0.5rem"
            }}>
                Book a Consultation
              </Button>
            </Link>
            <Link to="#" className="group inline-flex items-center gap-2 text-sm font-medium text-foreground underline-offset-4 hover:underline">
              Explore Our Services
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {trustPills.map((pill, i) => {
            const Icon = pill.icon;
            return <div key={i} data-index={i} className="inline-flex items-center gap-2 border border-border bg-background/70 px-4 py-2.5 text-xs font-medium text-foreground backdrop-blur-sm sm:text-sm" style={{
              borderRadius: "0.5rem"
            }}>
                  <Icon className="h-3.5 w-3.5 text-[var(--chart-2)]" style={{
                color: "hsl(43 46% 55%)"
              }} />
                  <span>{pill.label}</span>
                </div>;
          })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>;
}